import { useCallback } from 'react';

import { useNewRelicBrowserAgent } from '@notino/react-observability';

import { NEW_RELIC_OWNER } from './../constants';
import { ELogLevel, TLogError, TLogInformation, TLogWarning, TLoggerEvent } from './model';

/**
 * Custom React hook that returns a memoized logger function with Sentry and New Relic agent integration.
 *
 * Internally uses `useNewRelicBrowserAgent` to access the browser agent and passes it to the appropriate
 * log handler based on the log level provided.
 *
 * @returns A stable callback function to log events using Sentry and the New Relic agent.
 *
 * @example
 * const callLogger = useCallLogger();
 * callLogger({ logLevel: ELogLevel.ERROR, logError: new Error('Something failed') });
 *
 * @typeParam TLoggerEvent - A discriminated union or object that includes at least the `logLevel` property.
 */
export const useCallLogger = () => {
  const agent = useNewRelicBrowserAgent();

  return useCallback(
    (loggerInfo: TLoggerEvent) => {
      switch (loggerInfo.logLevel) {
        case ELogLevel.ERROR:
          captureError(loggerInfo, agent);
          break;
        case ELogLevel.WARNING:
          captureWarning(loggerInfo, agent);
          break;
        case ELogLevel.INFORMATION:
          captureInformation(loggerInfo, agent);
          break;
      }
    },
    [agent],
  );
};

const captureError = ({ logError }: TLogError, agent: BrowserAgent) => {
  if (agent) {
    agent.noticeError(logError, {
      owner: NEW_RELIC_OWNER,
    });
  }
};

const captureInformation = (info: TLogInformation, agent: BrowserAgent) => internalCapture(info, agent);

const captureWarning = (warning: TLogWarning, agent: BrowserAgent) => internalCapture(warning, agent);

const internalCapture = ({ logLevel, logMessage }: TLogInformation | TLogWarning, agent: BrowserAgent) => {
  if (agent) {
    agent.noticeError(`${logLevel}: ${logMessage}`, {
      owner: NEW_RELIC_OWNER,
    });
  }
};

type BrowserAgent = ReturnType<typeof useNewRelicBrowserAgent>;
